import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ShieldCheck, Lock, KeyRound, Database, Bug, Activity, Server, FileCheck2 } from 'lucide-react';
import SEO from '../components/SEO';
import TrustSubNav from '../components/TrustSubNav';
import { CompaniesOfficeBlock, CompaniesOfficeStrip } from '../components/CompaniesOfficeBlock';

// =============================================================================
// GIWAHS — Trust Centre · Security page (review-only)
// Source of truth: /app/memory/founder_vault/38_GIWAHS_Security_Page.md
// =============================================================================

const TrustSecurity = () => {
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Trust Centre', url: '/trust' },
    { name: 'Security', url: '/trust/security' },
  ];

  return (
    <div data-testid="trust-security-page" className="bg-white">
      <SEO
        title="Security — Trust Centre"
        description="GIWAHS security posture in writing: TLS 1.2+, AES-256 at rest, MFA + SSO + RBAC, daily encrypted backups, 4-tier incident severity model with 72-hour customer breach notification, SOC 2 / ISO 27001 roadmap."
        breadcrumbs={breadcrumbs}
      />

      {/* Hero */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <Link to="/trust" className="text-xs uppercase tracking-wider text-neutral-500 font-mono hover:text-neutral-900">
            ← Trust Centre
          </Link>
          <span className="mt-6 block label-overline">/ Security</span>
          <h1
            className="mt-4 max-w-4xl font-display tracking-tighter"
            style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 6vw, 5rem)', lineHeight: 0.95 }}
          >
            Security at GIWAHS isn't a department.<br />
            <span className="text-neutral-500">It's a contract.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-neutral-700 md:text-lg">
            We protect Founder Partner data with the same rigour we expect of the suppliers we
            verify. This page is the public summary; the Security &amp; Incident Response Policy
            in our document library is the full record.
          </p>
        </div>
      </section>

      <TrustSubNav />

      {/* Status block */}
      <section className="border-b border-black bg-[#F5F5F0]">
        <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-16">
          <div className="grid gap-px bg-black md:grid-cols-3">
            {[
              { l: 'Platform uptime SLA target', v: '99.95%' },
              { l: 'Last security review (internal)', v: 'Continuous — merge-time' },
              { l: 'Customer-impacting incidents (12 mo.)', v: '0' },
            ].map((s) => (
              <div key={s.l} className="bg-white p-6">
                <div className="label-overline tracking-wider">{s.l}</div>
                <div className="mt-3 font-display text-xl tracking-tight" style={{ fontWeight: 600 }}>{s.v}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-neutral-500 font-mono">
            Need a current uptime SLA letter for procurement? Email security@giwahs.com.
          </p>
        </div>
      </section>

      {/* Section grid: Access, Crypto, Network, App, Data */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ 01 — Controls in production</span>
          <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>
            Concrete controls, not adjectives.
          </h2>
          <div className="mt-12 grid gap-px bg-black md:grid-cols-2">
            {[
              {
                icon: KeyRound,
                title: 'Access control',
                bullets: [
                  'MFA + SSO mandatory on production systems (Supabase, Stripe, Cloudflare, GitHub, Google Workspace, Resend, OpenAI org).',
                  'Role-based access via Supabase Row-Level Security and SECURITY DEFINER RPCs.',
                  'Quarterly access reviews; annual full review; joiner/mover/leaver SLA 24 hours.',
                ],
              },
              {
                icon: Lock,
                title: 'Cryptography',
                bullets: [
                  'TLS 1.2+ in transit; AES-256 at rest.',
                  'Argon2id / bcrypt for passwords; never plaintext.',
                  'Short-lived JWTs (1h access / 30d refresh); HMAC-SHA256 webhook signatures.',
                ],
              },
              {
                icon: Server,
                title: 'Network & edge',
                bullets: [
                  'Cloudflare WAF, DDoS mitigation, bot management, rate limiting, IP-reputation filtering.',
                  'Restricted egress for backend processes — only documented Subprocessors.',
                  'Stripe (PCI-DSS Level 1) handles all card data — GIWAHS never receives raw cards.',
                ],
              },
              {
                icon: ShieldCheck,
                title: 'Application security',
                bullets: [
                  'Code review on every change touching auth, billing, RLS, or PII.',
                  'Automated dependency scanning; critical patches ≤ 7 days.',
                  'OWASP Top 10 review annually; CSRF + scoped CORS; webhook signature verification.',
                ],
              },
              {
                icon: Database,
                title: 'Data protection',
                bullets: [
                  'Tenant isolation via per-org RLS policies.',
                  'Daily encrypted backups with ≥ 35-day retention; annual restore test.',
                  'AI usage: no-training / zero-data-retention endpoints only. Currently disabled at platform level.',
                ],
              },
              {
                icon: Activity,
                title: 'Logging & monitoring',
                bullets: [
                  'Application logs retained 13 months; PII redacted by default.',
                  'Database audit log via audit_log table (migrations 015–020).',
                  'Alerts on failed-login spikes, webhook signature failures, cron non-completion.',
                ],
              },
            ].map((b) => (
              <div key={b.title} className="bg-white p-7">
                <b.icon size={22} strokeWidth={1.5} />
                <div className="mt-5 font-display text-xl tracking-tight" style={{ fontWeight: 600 }}>
                  {b.title}
                </div>
                <ul className="mt-3 space-y-2 text-sm leading-relaxed text-neutral-700">
                  {b.bullets.map((x) => <li key={x}>· {x}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BCDR table */}
      <section className="border-b border-black bg-[#F5F5F0]">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ 02 — Business continuity & recovery</span>
          <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>
            Recovery targets, in writing.
          </h2>
          <div className="mt-10 overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse border border-black bg-white text-sm">
              <thead className="bg-neutral-100">
                <tr>
                  <th className="border border-black p-4 text-left label-overline">Component</th>
                  <th className="border border-black p-4 text-left label-overline">RTO</th>
                  <th className="border border-black p-4 text-left label-overline">RPO</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Public landing pages', '< 1 hour', '0 (static, redeployable)'],
                  ['Backend API', '< 4 hours', '< 5 min (managed DB streaming)'],
                  ['Database', '< 8 hours', '< 24 hours (worst case)'],
                  ['Email dispatch (Resend)', '< 4 hours; degraded logs-only without data loss', '0'],
                  ['Billing & subscription state', 'Stripe-managed (99.99%+)', '0'],
                ].map((row) => (
                  <tr key={row[0]}>
                    <td className="border border-black p-4 font-medium">{row[0]}</td>
                    <td className="border border-black p-4 text-neutral-700">{row[1]}</td>
                    <td className="border border-black p-4 text-neutral-700">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-neutral-500 font-mono">
            Tabletop exercises run annually. Lessons-learned tracked to closure.
          </p>
        </div>
      </section>

      {/* IR + disclosure + roadmap */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:grid-cols-2 md:px-8 md:py-24">
          <div>
            <span className="label-overline">/ 03 — Incident response</span>
            <h3 className="mt-3 font-display text-2xl tracking-tight" style={{ fontWeight: 600 }}>
              When something goes wrong, this is what happens.
            </h3>
            <ul className="mt-6 space-y-3 text-sm leading-relaxed text-neutral-700">
              <li>
                <strong>4-tier severity model.</strong> SEV-1 (confirmed breach, prod down, active
                exploitation) through SEV-4 (informational).
              </li>
              <li>
                <strong>6-phase lifecycle.</strong> Identification → Containment → Eradication →
                Recovery → Notification → Lessons Learned.
              </li>
              <li>
                <strong>72-hour Customer notification</strong> of a Personal Data Breach affecting
                Customer Personal Data.
              </li>
              <li>
                Regulatory paths: GDPR Articles 33–34 · NZ NotifyUs · AU NDB / OAIC · US state breach laws.
              </li>
            </ul>
          </div>

          <div>
            <span className="label-overline">/ 04 — Vulnerability disclosure</span>
            <h3 className="mt-3 font-display text-2xl tracking-tight" style={{ fontWeight: 600 }}>
              Safe-harbour for good-faith research.
            </h3>
            <ul className="mt-6 space-y-3 text-sm leading-relaxed text-neutral-700">
              <li>Report to <a href="mailto:security@giwahs.com" className="underline">security@giwahs.com</a>.</li>
              <li>Acknowledgement within 2 business days.</li>
              <li>Triage within 5 business days.</li>
              <li>Remediation target: Critical ≤ 7 days · High ≤ 30 days · Medium ≤ 90 days.</li>
            </ul>
            <a
              data-testid="security-policy-download"
              href="/legal/security.html"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 border-b border-neutral-900 pb-1 text-sm font-medium hover:gap-3 transition-all"
            >
              <Bug size={14} strokeWidth={1.5} /> Open the Security & Incident Response Policy <ArrowUpRight size={14} strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="border-b border-black bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline text-neutral-400">/ 05 — Roadmap</span>
          <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>
            Where we're going, transparently.
          </h2>
          <div className="mt-10 grid gap-px bg-neutral-700 md:grid-cols-4">
            {[
              { y: 'Year 1', t: 'Policies in force', d: 'Documented runbooks. Vendor SOC 2 reliance.' },
              { y: 'Year 1–2', t: 'SOC 2 Type I → II', d: 'Independent audit cycle initiated.' },
              { y: 'Year 2–3', t: 'ISO/IEC 27001', d: 'Gap analysis after SOC 2 Type I.' },
              { y: 'Continuous', t: 'Privacy compliance', d: 'GDPR · UK GDPR · NZ Privacy · AU Privacy · CCPA reviews conducted with external legal advisors.' },
            ].map((p) => (
              <div key={p.y} className="bg-[#0A0A0A] p-6">
                <div className="label-overline text-neutral-500">{p.y}</div>
                <div className="mt-4 font-display text-xl tracking-tight" style={{ fontWeight: 600 }}>{p.t}</div>
                <p className="mt-3 text-sm leading-relaxed text-neutral-400">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Doc cross-links */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-6 px-5 py-12 md:px-8">
          <div className="text-sm text-neutral-600">
            See also: <Link to="/trust/privacy" className="underline">Privacy</Link> ·{' '}
            <Link to="/trust/compliance" className="underline">Compliance</Link> ·{' '}
            <Link to="/trust" className="underline">Trust Centre overview</Link>
          </div>
          <Link
            to="/founding-partners"
            className="inline-flex items-center gap-2 border-b border-neutral-900 pb-1 text-sm font-medium hover:gap-3 transition-all"
          >
            Back to Founding Partners <ArrowUpRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      {/* Companies Office verification (D-057) */}
      <CompaniesOfficeBlock />
      <CompaniesOfficeStrip />
    </div>
  );
};

export default TrustSecurity;
